import { useState } from "react";
import { MagnifyingGlassIcon, FunnelIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import LegislativeDocumentTable from "./table/LegislationDocumentTable";
import HistoryArchivesTable from "./table/HistoryArchivesTable";
import PublicConsultantsTable from "./table/PublicConsultantsTable";
import ResolutionOrdinanceTable from "./table/ResolutionOrdinanceTable";
// import SearchBar from "../common/SearchBar";

function LegislationTable() {
  const [activeTab, setActiveTab] = useState("Resolution & Ordinance");
  const [search, setSearch] = useState("");

  // Filter states per tab - separate them to avoid interference
  const [showFilterResolution, setShowFilterResolution] = useState(false);
  const [showFilterHistory, setShowFilterHistory] = useState(false);
  const [showFilterPublic, setShowFilterPublic] = useState(false);
  const [showFilterLegislative, setShowFilterLegislative] = useState(false);

  // Resolution & Ordinance - add separate open states
  const [typeOpenRO, setTypeOpenRO] = useState(false);
  const [committeeOpenRO, setCommitteeOpenRO] = useState(false);
  const [statusOpenRO, setStatusOpenRO] = useState(false);

  // Resolution & Ordinance filters
  const [typeFilter, setTypeFilter] = useState("");
  const [committeeFilter, setCommitteeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // History & Archives 
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");

  // Public Consultants 
  const [publicStartDate, setPublicStartDate] = useState("");
  const [publicEndDate, setPublicEndDate] = useState("");

  // Legislative Document - add separate open states
  const [typeOpenLD, setTypeOpenLD] = useState(false);
  const [committeeOpenLD, setCommitteeOpenLD] = useState(false);

  // Legislative Document filters
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [versionFilter, setVersionFilter] = useState("");

  const tabs = [
    "Resolution & Ordinance",
    "Legislative Document",
    "History & Archives",
    "Public Consultants",
  ];

  const header = (
    <>
      <div className="flex items-center justify-between ">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setShowFilterResolution(false);
                setShowFilterHistory(false);
                setShowFilterPublic(false);
                setShowFilterLegislative(false);
              }}
              className={`px-1 py-2 text-sm font-medium transition-colors duration-200 relative ${activeTab === tab
                ? "text-blue-600"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            {/* <SearchBar
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="block"
            /> */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search legislation..."
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-64"
            />
          </div>

          {activeTab === "Resolution & Ordinance" && (
            <button
              onClick={() => setShowFilterResolution(!showFilterResolution)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              Filter
            </button>
          )}
          {activeTab === "Legislative Document" && (
            <button
              onClick={() => setShowFilterLegislative(!showFilterLegislative)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              Filter
            </button>
          )}
          {activeTab === "History & Archives" && (
            <button
              onClick={() => setShowFilterHistory(!showFilterHistory)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              Filter
            </button>
          )}
          {activeTab === "Public Consultants" && (
            <button
              onClick={() => setShowFilterPublic(!showFilterPublic)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              Filter
            </button>
          )}
        </div>
      </div>

      {activeTab === "Resolution & Ordinance" && showFilterResolution && (
        <div className="pt-5 pb-2 animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 w-full appearance-none"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                onFocus={() => setTypeOpenRO(true)}
                onBlur={() => setTypeOpenRO(false)}
              >
                <option value="">All Types</option>
                <option value="Resolution">Resolution</option>
                <option value="Ordinance">Ordinance</option>
              </select>
              {typeOpenRO ? (
                <ChevronUpIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium mb-1">Committee</label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 w-full appearance-none"
                value={committeeFilter}
                onChange={(e) => setCommitteeFilter(e.target.value)}
                onFocus={() => setCommitteeOpenRO(true)}
                onBlur={() => setCommitteeOpenRO(false)}
              >
                <option value="">All Committees</option>
                <option value="Sports Committee">Sports Committee</option>
                <option value="Rescue Ops">Rescue Ops</option>
                <option value="Infrastructure">Infrastructure</option>
                <option value="Education Committee">Education Committee</option>
              </select>
              {committeeOpenRO ? (
                <ChevronUpIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 w-full appearance-none"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                onFocus={() => setStatusOpenRO(true)}
                onBlur={() => setStatusOpenRO(false)}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
                <option value="Pending">Pending</option>
                <option value="Archived">Archived</option>
                <option value="Approved">Approved</option>
              </select>
              {statusOpenRO ? (
                <ChevronUpIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Date Range</label>
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  type="date"
                  className="border border-gray-300 rounded-md px-3 py-2 w-full"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className="border border-gray-300 rounded-md px-3 py-2 w-full"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "Legislative Document" && showFilterLegislative && (
        <div className="pt-5 pb-2 animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            {/* Created By */}
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Created By</label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 w-full appearance-none"
                value={createdByFilter}
                onChange={(e) => setCreatedByFilter(e.target.value)}
                onFocus={() => setTypeOpenLD(true)}
                onBlur={() => setTypeOpenLD(false)}
              >
                <option value="">All Author/Main Sponsors</option>
                <option value="Admin User">Admin User</option>
                <option value="Author2">Author 2</option>
              </select>
              {typeOpenLD ? (
                <ChevronUpIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium mb-1">Version</label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 w-full appearance-none"
                value={versionFilter}
                onChange={(e) => setVersionFilter(e.target.value)}
                onFocus={() => setCommitteeOpenLD(true)}
                onBlur={() => setCommitteeOpenLD(false)}
              >
                <option value="">All Versions</option>
                <option value="1">Version 1</option>
                <option value="2">Version 2</option>
              </select>
              {committeeOpenLD ? (
                <ChevronUpIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 absolute right-3 top-2/3 transform -translate-y-1/2 pointer-events-none text-gray-400" />
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "History & Archives" && showFilterHistory && (
        <div className="pt-5 pb-2 animate-fadeIn">
          <label className="block text-sm font-medium mb-1">Date Range:</label>
          <div className="flex items-center gap-3">
            <input
              type="date"
              className="border border-gray-300 rounded-md px-3 py-2 w-full"
              value={historyStartDate}
              onChange={(e) => setHistoryStartDate(e.target.value)}
            />
            <span className="text-gray-600 font-semibold">-</span>
            <input
              type="date"
              className="border border-gray-300 rounded-md px-3 py-2 w-full"
              value={historyEndDate}
              onChange={(e) => setHistoryEndDate(e.target.value)}
            />
          </div>
        </div>
      )}

      {activeTab === "Public Consultants" && showFilterPublic && (
        <div className="pt-5 pb-2 animate-fadeIn">
          <label className="block text-sm font-medium mb-1">Date Range:</label>
          <div className="flex items-center gap-3 ">
            <input
              type="date"
              className="border border-gray-300 rounded-md px-3 py-2 w-full"
              value={publicStartDate}
              onChange={(e) => setPublicStartDate(e.target.value)}
            />
            <span className="text-gray-600 font-semibold">-</span>
            <input
              type="date"
              className="border border-gray-300 rounded-md px-3 py-2 w-full"
              value={publicEndDate}
              onChange={(e) => setPublicEndDate(e.target.value)}
            />
          </div>

        </div>
      )}
    </>
  )

  return (
    <div>
      {activeTab === "Resolution & Ordinance" && (
        <ResolutionOrdinanceTable
          search={search}
          typeFilter={typeFilter}
          committeeFilter={committeeFilter}
          statusFilter={statusFilter}
          startDate={startDate}
          endDate={endDate}
          header={header}
          activeTab={activeTab}
        />
      )}
      {activeTab === "Legislative Document" && (
        <LegislativeDocumentTable
          search={search}
          createdByFilter={createdByFilter}
          versionFilter={versionFilter}
          header={header}
          activeTab={activeTab}
        />
      )}

      {activeTab === "History & Archives" && (
        <HistoryArchivesTable
          search={search}
          startDate={historyStartDate}
          endDate={historyEndDate}
          header={header}
          activeTab={activeTab}
        />
      )}
      {activeTab === "Public Consultants" && (
        <PublicConsultantsTable
          search={search}
          startDate={publicStartDate}
          endDate={publicEndDate}
          header={header}
          activeTab={activeTab}
        />
      )}
    </div>
  );
}

export default LegislationTable;